import { lazy, Suspense, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';
import { createBrowserRouter, createMemoryRouter, Navigate, type RouteObject } from 'react-router-dom';
import { Shell } from './layout/Shell';
import { RequireAuth } from './RequireAuth';
import { RequireSubscription } from '../subscription/RequireSubscription';
import { ChoosePlanPage } from '../subscription/ChoosePlanPage';
import { RequirePermission } from './RequirePermission';
import { LoginPage } from '../auth/LoginPage';
import { RegisterPage } from '../auth/RegisterPage';
import { AcceptInvitationPage } from '../auth/AcceptInvitationPage';
import { SelectOrgPage } from '../auth/SelectOrgPage';
import { NAV_ITEMS, type NavItem, type ZoneId } from './nav';
import { Skeleton } from '../ui';

/**
 * Déclaration des routes. Chaque zone est importée en lazy depuis features/<zone>/index.tsx :
 * des équipes différentes peuvent donc travailler en parallèle sans conflit de fichier.
 * Pour ajouter une zone : une ligne dans `zones` + une entrée dans nav.ts.
 */
const zones: Record<ZoneId, LazyExoticComponent<ComponentType>> = {
  dashboard: lazy(() => import('../features/dashboard')),
  fleet: lazy(() => import('../features/fleet')),
  people: lazy(() => import('../features/people')),
  policies: lazy(() => import('../features/policies')),
  audit: lazy(() => import('../features/audit')),
  satisfaction: lazy(() => import('../features/satisfaction')),
  deployment: lazy(() => import('../features/deployment')),
  billing: lazy(() => import('../features/billing')),
  settings: lazy(() => import('../features/settings')),
};

const ProfilePage = lazy(() => import('../features/profile'));

function PageFallback() {
  return (
    <div role="status" aria-busy="true" style={{ display: 'grid', gap: 16 }}>
      <Skeleton height={36} width={280} />
      <Skeleton height={160} radius={20} />
    </div>
  );
}

function zoneElement(Zone: ComponentType, permission: NavItem['permission'], mode?: 'all' | 'any'): ReactNode {
  return (
    <RequirePermission permission={permission} mode={mode}>
      <Suspense fallback={<PageFallback />}>
        <Zone />
      </Suspense>
    </RequirePermission>
  );
}

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/select-org', element: <SelectOrgPage /> },
  {
    element: <RequireAuth />,
    children: [
      // Le choix de l'offre précède toute autre page : sans offre active, la console est fermée.
      { path: '/choose-plan', element: <ChoosePlanPage /> },
      // Lien de l'e-mail d'invitation : hors garde d'abonnement (l'invité n'a que son organisation personnelle, au plan free).
      { path: '/invitations/:orgId/:invitationId', element: <AcceptInvitationPage /> },
      {
        element: <RequireSubscription />,
        children: [
      {
        element: <Shell />,
        children: [
          // Écran « Mon profil » (menu utilisateur), hors navigation latérale
          { path: 'profile', element: <Suspense fallback={<PageFallback />}><ProfilePage /></Suspense> },
          ...NAV_ITEMS.map((item) =>
            item.id === 'dashboard'
              ? { index: true, element: zoneElement(zones.dashboard, undefined) }
              : { path: `${item.path.slice(1)}/*`, element: zoneElement(zones[item.id], item.permission, item.mode) },
          ),
        ],
      },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];

export const createRouter = () => createBrowserRouter(routes);
/** Routeur en mémoire pour les tests. */
export const createTestRouter = (initialEntries: string[]) => createMemoryRouter(routes, { initialEntries });
